import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, DocumentArrowDownIcon, TruckIcon } from '@heroicons/react/20/solid';
import CreateDelivery from '../components/CreateDelivery';
import WorkOrderDrawer from '../components/WorkOrderDrawer';
import PackageDrawer from '../components/PackageDrawer';
import MaterialDrawer from '../components/MaterialDrawer';
import { StatusChip, CircularProgress } from '../components/ui';

function KanbanJob() {
  const { user } = useAuth();
  const { id, po_number: poNumberParam } = useParams();
  const [kanbanJobs, setKanbanJobs] = useState([]);
  const [poNumber, setPoNumber] = useState(poNumberParam || '');
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [isCreateWorkOrderModalOpen, setIsCreateWorkOrderModalOpen] = useState(false);
  const [isLogShipmentModalOpen, setIsLogShipmentModalOpen] = useState(false);
  const [isCreateDeliveryModalOpen, setIsCreateDeliveryModalOpen] = useState(false);
  const [isNewDeliveryModalOpen, setIsNewDeliveryModalOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [newWorkOrderLineItem, setNewWorkOrderLineItem] = useState(null);
  const [newQuantityOrdered, setNewQuantityOrdered] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [productionQuantity, setProductionQuantity] = useState('');
  const [shipmentQuantity, setShipmentQuantity] = useState('');
  const [deliveryQuantity, setDeliveryQuantity] = useState('');
  const [packageNumber, setPackageNumber] = useState('');
  const [shipDate, setShipDate] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [files, setFiles] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  

  const [isPdfPreviewModalOpen, setIsPdfPreviewModalOpen] = useState(false);
const [pdfUrl, setPdfUrl] = useState(null);
const [isDeleteWorkOrderModalOpen, setIsDeleteWorkOrderModalOpen] = useState(false);
const [workOrderToDelete, setWorkOrderToDelete] = useState(null);
const [isWorkOrderDrawerOpen, setIsWorkOrderDrawerOpen] = useState(false);
const [selectedWorkOrderForDrawer, setSelectedWorkOrderForDrawer] = useState(null);
const [isPackageDrawerOpen, setIsPackageDrawerOpen] = useState(false);
const [selectedPackage, setSelectedPackage] = useState(null);
const [isEditPoModalOpen, setIsEditPoModalOpen] = useState(false);
const [selectedKanbanJob, setSelectedKanbanJob] = useState(null);
const [editedPoNumber, setEditedPoNumber] = useState('');
const [editedLineItems, setEditedLineItems] = useState([]);
const [newLineItem, setNewLineItem] = useState({
  po_line_number: '',
  part_number: '',
  description: '',
  quantity_on_order: '',
  unit_price: '',
  shop_id: '',
});
const [shops, setShops] = useState([]);
const [customers, setCustomers] = useState([]);
const [parts, setParts] = useState([]);
const [editedCustomerId, setEditedCustomerId] = useState('');
const [partsSearchQuery, setPartsSearchQuery] = useState('');
const [lineItemsToDelete, setLineItemsToDelete] = useState([]);
const [isDeletePoModalOpen, setIsDeletePoModalOpen] = useState(false);
const [deletePoConfirmation, setDeletePoConfirmation] = useState('');
const [materialDrawerOpen, setMaterialDrawerOpen] = useState(false);
const [selectedWorkOrderForMaterial, setSelectedWorkOrderForMaterial] = useState(null);


const handlePreviewPDF = async (filePath, bucket = 'delivery-files') => {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, 60);
      if (error) throw new Error(`Failed to generate signed URL: ${error.message}`);
  
      // Open the PDF in a new tab
      window.open(data.signedUrl, '_blank');
  
      // Refresh the current tab
      window.location.reload();
    } catch (err) {
      console.error('Error generating PDF preview URL:', err);
      toast.error(`Failed to load PDF preview: ${err.message}`);
    }
  };
  

const closePdfPreviewModal = () => {
  setIsPdfPreviewModalOpen(false);
  setPdfUrl(null);
};

useEffect(() => {
    const fetchShops = async () => {
      try {
        const { data, error } = await supabase
          .from('shops')
          .select('id, name')
          .order('name');
        if (error) throw error;
        setShops(data || []);
      } catch (err) {
        console.error('Error fetching shops:', err.message);
      }
    };

    const fetchCustomers = async () => {
      try {
        const { data, error } = await supabase
          .from('customers')
          .select('id, name, nickname')
          .order('nickname');
        if (error) throw error;
        setCustomers(data || []);
      } catch (err) {
        console.error('Error fetching customers:', err.message);
      }
    };

    const fetchParts = async () => {
      try {
        const { data, error } = await supabase
          .from('parts')
          .select('part_number, description, unit_price')
          .order('part_number');
        if (error) throw error;
        setParts(data || []);
      } catch (err) {
        console.error('Error fetching parts:', err.message);
      }
    };

    fetchShops();
    fetchCustomers();
    fetchParts();
  }, []);

  useEffect(() => {
    document.title = `Kanban PO #${poNumber || ''}`;
  }, [poNumber]);


 useEffect(() => {
  const fetchKanbanJob = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Determine PO number - either from URL param or by looking up the id
      let targetPoNumber = poNumberParam;

      if (!targetPoNumber && id) {
        // Old route with numeric id - look up the po_number
        const { data: jobData, error: jobError } = await supabase
          .from('kanban_jobs')
          .select('po_number')
          .eq('id', id)
          .single();
        if (jobError) throw jobError;
        if (!jobData) throw new Error('Kanban job not found');
        targetPoNumber = jobData.po_number;
      }

      if (!targetPoNumber) throw new Error('No PO number provided');
      setPoNumber(targetPoNumber);

      const { data: jobsData, error: jobsError } = await supabase
        .from('kanban_jobs')
        .select(`
          id, po_number, po_line_number, part_number, description, status,
          quantity_on_order, produced_quantity, shipped_quantity,
          po_start_date, unit_price,
          shops(id, name), customers(id, nickname, name,
            billing_address, billing_city, billing_state, billing_zip,
            billing_contact_name, billing_contact_email, billing_contact_phone,
            shipping_address, shipping_city, shipping_state, shipping_zip,
            shipping_contact_name, shipping_contact_email, shipping_contact_phone, net_terms),
          parts!inner(part_number, part_number_master, revision, inventory_quantity)
        `)
        .eq('po_number', targetPoNumber)
        .order('po_line_number');
      if (jobsError) throw jobsError;

      const formattedJobs = jobsData.map(job => ({
        id: job.id,
        po_number: job.po_number,
        po_line_number: job.po_line_number,
        part_number: job.part_number,
        part_number_master: job.parts?.part_number_master || 'N/A',
        description: job.description,
        status: job.status,
        quantity_on_order: job.quantity_on_order,
        produced_quantity: job.produced_quantity,
        shipped_quantity: job.shipped_quantity,
        po_start_date: job.po_start_date,
        unit_price: job.unit_price || 0,
        shop_name: job.shops?.name || 'Unknown',
        customer_name: job.customers?.nickname || 'Unknown',
        customers: job.customers, // Changed from 'customer' to 'customers' to match expected key
        inventory_quantity: job.parts?.inventory_quantity || 0,
        parts: job.parts, // Preserve the entire parts object
      }));
      setKanbanJobs(formattedJobs);

      const { data: workOrdersData, error: workOrdersError } = await supabase
        .from('jobs')
        .select(`
          id, kanban_job_id, quantity_on_order, produced_quantity, shipped_quantity,
          expected_delivery_date, status, po_number, po_line_number, part_number,
          material_status, material_notes,
          kanban_jobs!inner(id, po_number, po_line_number, part_number)
        `)
        .in('kanban_job_id', formattedJobs.map(job => job.id))
        .order('expected_delivery_date', { ascending: true });
      if (workOrdersError) throw workOrdersError;
      setWorkOrders(workOrdersData || []);

      const { data: filesData, error: filesError } = await supabase
        .from('job_files')
        .select('*')
        .eq('po_number', targetPoNumber);
      if (filesError) throw filesError;
      setFiles(filesData || []);

      const { data: deliveriesData, error: deliveriesError } = await supabase
        .from('deliveries')
        .select('delivery_id, package_number, quantity, ship_date, invoice_number, packing_slip_path, job_id, kanban_job_id')
        .in('kanban_job_id', formattedJobs.map(job => job.id));
      if (deliveriesError) throw deliveriesError;
      setDeliveries(deliveriesData || []);
    } catch (err) {
      console.error('Error fetching Kanban job:', err.message);
      toast.error(`Failed to fetch Kanban job: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  fetchKanbanJob();
}, [user, id, poNumberParam]);

  const generateWorkOrderNumber = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const chars = [
      letters[Math.floor(Math.random() * letters.length)],
      letters[Math.floor(Math.random() * letters.length)],
      numbers[Math.floor(Math.random() * numbers.length)],
      numbers[Math.floor(Math.random() * numbers.length)],
    ];
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return `KAN${chars.join('')}`;
  };

  const getNextPoLineNumber = async () => {
    const { data, error } = await supabase
      .from('jobs')
      .select('po_line_number')
      .in('kanban_job_id', kanbanJobs.map(job => job.id))
      .order('po_line_number', { ascending: false })
      .limit(1);
    if (error) throw new Error(`Failed to fetch work orders: ${error.message}`);
    return data.length > 0 ? parseInt(data[0].po_line_number) + 1 : 1;
  };

  const openCreateWorkOrderModal = () => {
    setNewWorkOrderLineItem(null);
    setNewQuantityOrdered('');
    setNewDueDate('');
    setIsCreateWorkOrderModalOpen(true);
  };

  const closeCreateWorkOrderModal = () => {
    setIsCreateWorkOrderModalOpen(false);
    setNewWorkOrderLineItem(null);
    setNewQuantityOrdered('');
    setNewDueDate('');
  };

  const handleCreateWorkOrder = async () => {
    if (!newWorkOrderLineItem || !newQuantityOrdered || newQuantityOrdered <= 0 || !newDueDate) {
      toast.error('Please fill in all fields with valid values.');
      return;
    }

    try {
      const { data: kanbanJobData, error: fetchKanbanError } = await supabase
        .from('kanban_jobs')
        .select('unit_price')
        .eq('id', newWorkOrderLineItem.id)
        .single();
      if (fetchKanbanError) throw new Error(`Failed to fetch Kanban job: ${fetchKanbanError.message}`);

      if (!kanbanJobData || kanbanJobData.unit_price == null) {
        throw new Error('Unit price not found for the selected Kanban job.');
      }

      const workOrderNumber = generateWorkOrderNumber();
      const workOrderLineNumber = await getNextPoLineNumber();

      const { data, error } = await supabase
        .from('jobs')
        .insert({
          kanban_job_id: newWorkOrderLineItem.id,
          po_number: workOrderNumber,
          po_line_number: workOrderLineNumber,
          part_number: newWorkOrderLineItem.part_number,
          description: newWorkOrderLineItem.description,
          quantity_on_order: parseInt(newQuantityOrdered),
          produced_quantity: 0,
          shipped_quantity: 0,
          expected_delivery_date: newDueDate,
          status: 'not_started',
          shop_id: newWorkOrderLineItem.shops?.id || 1,
          customer_id: newWorkOrderLineItem.customers?.id || 1,
          unit_price: kanbanJobData.unit_price,
        })
        .select(`
          id, kanban_job_id, quantity_on_order, produced_quantity, shipped_quantity, 
          expected_delivery_date, status, po_number, po_line_number,
          kanban_jobs!inner(id, po_number, po_line_number, part_number)
        `)
        .single();
      if (error) throw new Error(`Failed to create work order: ${error.message}`);

      setWorkOrders([...workOrders, data]);
      closeCreateWorkOrderModal();
      toast.success('Work order created successfully!');
    } catch (err) {
      console.error('Error creating work order:', err);
      toast.error(`Failed to create work order: ${err.message}`);
    }
  };

  const handleDeleteWorkOrder = async () => {
    if (!workOrderToDelete) return;

    try {
      // Check if work order has any deliveries
      const { data: deliveriesForWo, error: deliveriesCheckError } = await supabase
        .from('deliveries')
        .select('package_number')
        .eq('job_id', workOrderToDelete.id)
        .limit(1);

      if (deliveriesCheckError) throw new Error(`Failed to check deliveries: ${deliveriesCheckError.message}`);

      if (deliveriesForWo && deliveriesForWo.length > 0) {
        toast.error(`Cannot delete: Work order has delivery ${deliveriesForWo[0].package_number}. Delete the delivery first.`);
        setIsDeleteWorkOrderModalOpen(false);
        setWorkOrderToDelete(null);
        return;
      }

      // Check if work order has any production logs
      const { data: productionLogs, error: productionCheckError } = await supabase
        .from('production_logs')
        .select('log_id')
        .eq('job_id', workOrderToDelete.id)
        .limit(1);

      if (productionCheckError) throw new Error(`Failed to check production logs: ${productionCheckError.message}`);

      if (productionLogs && productionLogs.length > 0) {
        toast.error('Cannot delete: Work order has production history. Delete the production logs first.');
        setIsDeleteWorkOrderModalOpen(false);
        setWorkOrderToDelete(null);
        return;
      }

      // Delete the work order
      const { data: deleteData, error: deleteError } = await supabase
        .from('jobs')
        .delete()
        .eq('id', workOrderToDelete.id)
        .select();

      if (deleteError) throw new Error(`Failed to delete work order: ${deleteError.message}`);

      if (!deleteData || deleteData.length === 0) {
        throw new Error('No rows deleted - check database permissions');
      }

      // Update local state
      setWorkOrders(workOrders.filter(wo => wo.id !== workOrderToDelete.id));
      setIsDeleteWorkOrderModalOpen(false);
      setWorkOrderToDelete(null);
      toast.success('Work order deleted successfully!');
    } catch (err) {
      console.error('Error deleting work order:', err);
      toast.error(`Failed to delete work order: ${err.message}`);
    }
  };

  const handleDeleteDelivery = async (delivery) => {
    if (!window.confirm(`Delete delivery ${delivery.package_number}? This cannot be undone.`)) return;

    try {
      // Get the delivery details to update quantities
      const { data: deliveryData, error: fetchError } = await supabase
        .from('deliveries')
        .select('*')
        .eq('package_number', delivery.package_number);

      if (fetchError) throw new Error(`Failed to fetch delivery: ${fetchError.message}`);

      // Update shipped quantities for each item in the delivery
      for (const item of deliveryData) {
        if (item.kanban_job_id) {
          // Update kanban job shipped quantity
          const kanbanJob = kanbanJobs.find(j => j.id === item.kanban_job_id);
          if (kanbanJob) {
            const newShipped = Math.max(0, kanbanJob.shipped_quantity - item.quantity);
            await supabase
              .from('kanban_jobs')
              .update({ shipped_quantity: newShipped, updated_at: new Date().toISOString() })
              .eq('id', item.kanban_job_id);
          }
        }
        if (item.job_id) {
          // Update work order shipped quantity
          const workOrder = workOrders.find(wo => wo.id === item.job_id);
          if (workOrder) {
            const newShipped = Math.max(0, workOrder.shipped_quantity - item.quantity);
            await supabase
              .from('jobs')
              .update({ shipped_quantity: newShipped, updated_at: new Date().toISOString() })
              .eq('id', item.job_id);
          }
        }
      }

      // Delete the delivery records
      const { error: deleteError } = await supabase
        .from('deliveries')
        .delete()
        .eq('package_number', delivery.package_number);

      if (deleteError) throw new Error(`Failed to delete delivery: ${deleteError.message}`);

      // Update local state
      setDeliveries(deliveries.filter(d => d.package_number !== delivery.package_number));

      // Refresh to get updated quantities
      await refreshKanbanJobs();

      toast.success(`Delivery ${delivery.package_number} deleted successfully!`);
    } catch (err) {
      console.error('Error deleting delivery:', err);
      toast.error(`Failed to delete delivery: ${err.message}`);
    }
  };

  const openMaterialDrawer = (workOrder) => {
    setSelectedWorkOrderForMaterial(workOrder);
    setMaterialDrawerOpen(true);
  };

  const handleMaterialUpdate = (updatedJob) => {
    setWorkOrders(prev => prev.map(wo =>
      wo.id === updatedJob.id
        ? { ...wo, material_status: updatedJob.material_status, material_notes: updatedJob.material_notes }
        : wo
    ));
  };

  const handleDeleteKanbanJob = () => {
    setDeletePoConfirmation('');
    setIsDeletePoModalOpen(true);
  };

  const confirmDeleteKanbanJob = async () => {
    if (deletePoConfirmation !== poNumber) {
      toast.error('PO number does not match. Please enter the correct PO number to confirm deletion.');
      return;
    }

    try {
      // Delete associated work orders (jobs)
      const { error: workOrdersError } = await supabase
        .from('jobs')
        .delete()
        .in('kanban_job_id', kanbanJobs.map(job => job.id));
      if (workOrdersError) throw new Error(`Failed to delete associated work orders: ${workOrdersError.message}`);
  
      // Delete associated deliveries
      const { error: deliveriesError } = await supabase
        .from('deliveries')
        .delete()
        .in('kanban_job_id', kanbanJobs.map(job => job.id));
      if (deliveriesError) throw new Error(`Failed to delete associated deliveries: ${deliveriesError.message}`);
  
      // Delete all Kanban jobs under this PO
      const { error } = await supabase
        .from('kanban_jobs')
        .delete()
        .eq('po_number', poNumber);
      if (error) throw new Error(`Failed to delete Kanban Jobs: ${error.message}`);
  
      // Redirect to Kanban Jobs list page
      toast.success('Kanban PO deleted successfully!');
      window.location.href = '/kanban'; // Adjust the redirect URL as needed
    } catch (err) {
      console.error('Error deleting Kanban PO:', err.message);
      toast.error(`Failed to delete Kanban PO: ${err.message}`);
    }
  };

  const openEditPoModal = (kanbanJob) => {
    setSelectedKanbanJob(kanbanJob);
    setEditedPoNumber(kanbanJob.po_number);
    setEditedCustomerId(kanbanJob.customers?.id || '');
    setEditedLineItems(kanbanJobs.map(job => ({
      id: job.id,
      po_line_number: job.po_line_number,
      part_number: job.part_number,
      description: job.description,
      quantity_on_order: job.quantity_on_order,
      unit_price: job.unit_price || 0,
      shop_id: job.shops?.id || '',
    })));
    setNewLineItem({
      po_line_number: '',
      part_number: '',
      description: '',
      quantity_on_order: '',
      unit_price: '',
      shop_id: '',
    });
    setLineItemsToDelete([]);
    setPartsSearchQuery('');
    setIsEditPoModalOpen(true);
  };
  
  const closeEditPoModal = () => {
    setIsEditPoModalOpen(false);
    setSelectedKanbanJob(null);
    setEditedPoNumber('');
    setEditedCustomerId('');
    setEditedLineItems([]);
    setLineItemsToDelete([]);
    setNewLineItem({
      po_line_number: '',
      part_number: '',
      description: '',
      quantity_on_order: '',
      unit_price: '',
      shop_id: '',
    });
  };
  
  const handleEditPo = async () => {
    if (
      !editedPoNumber ||
      !editedCustomerId ||
      editedLineItems.some(
        item =>
          !item.part_number ||
          !item.quantity_on_order ||
          item.quantity_on_order <= 0 ||
          (item.unit_price !== undefined && (isNaN(item.unit_price) || item.unit_price < 0))
      )
    ) {
      toast.error('Please fill in all required fields with valid values.');
      return;
    }

    try {
      // Delete line items marked for deletion
      for (const itemId of lineItemsToDelete) {
        const { error } = await supabase
          .from('kanban_jobs')
          .delete()
          .eq('id', itemId);
        if (error) throw new Error(`Failed to delete line item: ${error.message}`);
      }

      // Separate existing and new line items
      const existingItems = editedLineItems.filter(item => !item.isNew && !lineItemsToDelete.includes(item.id));
      const newItems = editedLineItems.filter(item => item.isNew);

      // Update existing line items
      for (const item of existingItems) {
        const { error } = await supabase
          .from('kanban_jobs')
          .update({
            po_number: editedPoNumber,
            po_line_number: item.po_line_number,
            part_number: item.part_number,
            description: item.description,
            quantity_on_order: parseInt(item.quantity_on_order),
            unit_price: parseFloat(item.unit_price) || 0,
            shop_id: item.shop_id || null,
            customer_id: parseInt(editedCustomerId),
          })
          .eq('id', item.id);
        if (error) throw new Error(`Failed to update Kanban Job ${item.id}: ${error.message}`);
      }

      // Insert new line items added via the Add button
      // Get max line number from all existing kanban jobs for this PO (for auto-assign fallback)
      const maxExistingLineNumber = Math.max(...kanbanJobs.map(j => j.po_line_number || 0), ...newItems.filter(i => i.po_line_number).map(i => i.po_line_number), 0);
      let nextLineNumber = maxExistingLineNumber + 1;
      for (const item of newItems) {
        const lineNum = item.po_line_number || nextLineNumber++;
        const { error } = await supabase
          .from('kanban_jobs')
          .insert({
            po_number: editedPoNumber,
            po_line_number: lineNum,
            part_number: item.part_number,
            description: item.description,
            quantity_on_order: parseInt(item.quantity_on_order),
            unit_price: parseFloat(item.unit_price) || 0,
            produced_quantity: 0,
            shipped_quantity: 0,
            po_start_date: new Date().toISOString(),
            status: 'not_started',
            shop_id: item.shop_id || null,
            customer_id: parseInt(editedCustomerId),
          });
        if (error) throw new Error(`Failed to add new line item: ${error.message}`);
      }

      // Add new line item if it exists
      if (newLineItem.part_number && newLineItem.quantity_on_order) {
        if (newLineItem.unit_price && (isNaN(newLineItem.unit_price) || newLineItem.unit_price < 0)) {
          toast.error('Unit price for new line item must be a non-negative number.');
          return;
        }
        // Use provided line number or auto-assign next available
        const newItemLineNum = newLineItem.po_line_number || (Math.max(...editedLineItems.map(i => i.po_line_number || 0), 0) + 1);
        const { data, error } = await supabase
          .from('kanban_jobs')
          .insert({
            po_number: editedPoNumber,
            po_line_number: newItemLineNum,
            part_number: newLineItem.part_number,
            description: newLineItem.description,
            quantity_on_order: parseInt(newLineItem.quantity_on_order),
            unit_price: parseFloat(newLineItem.unit_price) || 0,
            produced_quantity: 0,
            shipped_quantity: 0,
            po_start_date: new Date().toISOString(),
            status: 'not_started',
            shop_id: newLineItem.shop_id || null,
            customer_id: parseInt(editedCustomerId),
          })
          .select(`
            id, po_number, po_line_number, part_number, description, status, 
            quantity_on_order, produced_quantity, shipped_quantity, 
            po_start_date, unit_price, 
            shops(id, name), customers(id, nickname, name, 
              billing_address, billing_city, billing_state, billing_zip, 
              billing_contact_name, billing_contact_email, billing_contact_phone,
              shipping_address, shipping_city, shipping_state, shipping_zip,
              shipping_contact_name, shipping_contact_email, shipping_contact_phone, net_terms),
            parts!inner(part_number, inventory_quantity, part_number_master, revision)
          `)
          .single();
        if (error) throw new Error(`Failed to add new Kanban Job: ${error.message}`);
  
        setKanbanJobs([...kanbanJobs, {
          id: data.id,
          po_number: data.po_number,
          po_line_number: data.po_line_number,
          part_number: data.part_number,
          description: data.description,
          status: data.status,
          quantity_on_order: data.quantity_on_order,
          unit_price: data.unit_price || 0,
          produced_quantity: data.produced_quantity,
          shipped_quantity: data.shipped_quantity,
          po_start_date: data.po_start_date,
          shop_name: data.shops?.name || 'Unknown',
          customer_name: data.customers?.nickname || 'Unknown',
          customer: data.customers,
          inventory_quantity: data.parts?.inventory_quantity || 0,
        }]);
      }
  
      // Refresh Kanban jobs to ensure consistency
      const { data: refreshedKanbanJobs, error: fetchError } = await supabase
        .from('kanban_jobs')
        .select(`
          id, po_number, po_line_number, part_number, description, status, 
          quantity_on_order, produced_quantity, shipped_quantity, 
          po_start_date, unit_price, 
          shops(id, name), customers(id, nickname, name, 
            billing_address, billing_city, billing_state, billing_zip, 
            billing_contact_name, billing_contact_email, billing_contact_phone,
            shipping_address, shipping_city, shipping_state, shipping_zip,
            shipping_contact_name, shipping_contact_email, shipping_contact_phone, net_terms),
          parts!inner(part_number, inventory_quantity, part_number_master, revision)
        `)
        .eq('po_number', editedPoNumber)
        .order('po_line_number');
      if (fetchError) throw new Error(`Failed to fetch updated Kanban jobs: ${fetchError.message}`);
  
      setKanbanJobs(refreshedKanbanJobs.map(job => ({
        id: job.id,
        po_number: job.po_number,
        po_line_number: job.po_line_number,
        part_number: job.part_number,
        description: job.description,
        status: job.status,
        quantity_on_order: job.quantity_on_order,
        produced_quantity: job.produced_quantity,
        shipped_quantity: job.shipped_quantity,
        po_start_date: job.po_start_date,
        unit_price: job.unit_price || 0,
        shop_name: job.shops?.name || 'Unknown',
        customer_name: job.customers?.nickname || 'Unknown',
        customer: job.customers,
        inventory_quantity: job.parts?.inventory_quantity || 0,
        parts: job.parts, // Preserve the entire parts object
      })) || []);
  
      setPoNumber(editedPoNumber);
      closeEditPoModal();
      toast.success('PO updated successfully!');
    } catch (err) {
      console.error('Error updating PO:', err);
      toast.error(`Failed to update PO: ${err.message}`);
    }
  };

  const openLogShipmentModal = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setShipmentQuantity('');
    setIsLogShipmentModalOpen(true);
  };

  const closeLogShipmentModal = () => {
    setIsLogShipmentModalOpen(false);
    setSelectedWorkOrder(null);
    setShipmentQuantity('');
  };

  const handleLogShipment = async () => {
    if (!shipmentQuantity || shipmentQuantity <= 0) {
      toast.error('Please enter a valid shipment quantity.');
      return;
    }

    const newShippedQuantity = selectedWorkOrder.shipped_quantity + parseInt(shipmentQuantity);
    if (newShippedQuantity > selectedWorkOrder.quantity_on_order) {
      toast.error('Shipped quantity cannot exceed ordered quantity.');
      return;
    }
    if (newShippedQuantity > selectedWorkOrder.produced_quantity) {
      toast.error('Shipped quantity cannot exceed produced quantity.');
      return;
    }

    try {
      const { error: workOrderError } = await supabase
        .from('jobs')
        .update({
          shipped_quantity: newShippedQuantity,
          status: newShippedQuantity === selectedWorkOrder.quantity_on_order ? 'closed' : 'partially_shipped',
        })
        .eq('id', selectedWorkOrder.id);
      if (workOrderError) throw new Error(`Failed to update work order: ${workOrderError.message}`);

      const { data: kanbanJobData, error: fetchKanbanError } = await supabase
        .from('kanban_jobs')
        .select('shipped_quantity')
        .eq('id', selectedWorkOrder.kanban_job_id)
        .single();
      if (fetchKanbanError) throw new Error(`Failed to fetch Kanban job: ${fetchKanbanError.message}`);

      const newKanbanShippedQuantity = kanbanJobData.shipped_quantity + parseInt(shipmentQuantity);
      const { error: updateKanbanError } = await supabase
        .from('kanban_jobs')
        .update({
          shipped_quantity: newKanbanShippedQuantity,
        })
        .eq('id', selectedWorkOrder.kanban_job_id);
      if (updateKanbanError) throw new Error(`Failed to update Kanban job: ${updateKanbanError.message}`);

      setWorkOrders(workOrders.map(wo =>
        wo.id === selectedWorkOrder.id
          ? { ...wo, shipped_quantity: newShippedQuantity }
          : wo
      ));
      setKanbanJobs(kanbanJobs.map(job =>
        job.id === selectedWorkOrder.kanban_job_id
          ? { ...job, shipped_quantity: newKanbanShippedQuantity }
          : job
      ));

      closeLogShipmentModal();
      toast.success('Shipment logged successfully!');
    } catch (err) {
      console.error('Error logging shipment:', err);
      toast.error(`Failed to log shipment: ${err.message}`);
    }
  };


  const closeCreateDeliveryModal = () => {
    setIsCreateDeliveryModalOpen(false);
    setSelectedWorkOrder(null);
    setDeliveryQuantity('');
    setPackageNumber('');
    setShipDate('');
    setInvoiceNumber('');
  };

  const handleCreateDelivery = async () => {
    if (!deliveryQuantity || deliveryQuantity <= 0 || !packageNumber || !shipDate) {
      toast.error('Please fill in all required fields with valid values.');
      return;
    }

    const deliveryQty = parseInt(deliveryQuantity);
    if (deliveryQty > selectedWorkOrder.shipped_quantity) {
      toast.error('Delivered quantity cannot exceed shipped quantity.');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('deliveries')
        .insert({
          kanban_job_id: selectedWorkOrder.kanban_job_id,
          package_number: packageNumber,
          quantity: deliveryQty,
          ship_date: shipDate,
          invoice_number: invoiceNumber || null,
        })
        .select()
        .single();
      if (error) throw new Error(`Failed to create delivery: ${error.message}`);

      setDeliveries([...deliveries, data]);
      closeCreateDeliveryModal();
      toast.success('Delivery created successfully!');
    } catch (err) {
      console.error('Error creating delivery:', err);
      toast.error(`Failed to create delivery: ${err.message}`);
    }
  };

  const openUploadModal = () => {
    setFileToUpload(null);
    setIsUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
    setFileToUpload(null);
  };

  const handleFileUpload = async () => {
    if (!fileToUpload) {
      toast.error('Please select a file to upload.');
      return;
    }
  
    try {
      // Upload the file to the job-files storage bucket
      const fileName = `${poNumber}/${Date.now()}_${fileToUpload.name}`;
      const { error: uploadError } = await supabase
        .storage
        .from('job-files')
        .upload(fileName, fileToUpload);
      if (uploadError) throw new Error(`Failed to upload file: ${uploadError.message}`);
  
      // Insert a record into the job_files table
      const { error: insertError } = await supabase
        .from('job_files')
        .insert({
          po_number: poNumber,
          file_name: fileToUpload.name,
          file_path: fileName,
          file_type: 'Other', // You can determine file_type based on file extension if needed
          uploaded_by: user.id,
          uploaded_at: new Date().toISOString(),
        });
      if (insertError) throw new Error(`Failed to insert file record: ${insertError.message}`);
  
      // Fetch the updated list of files for this PO
      const { data: filesData, error: filesError } = await supabase
        .from('job_files')
        .select('*')
        .eq('po_number', poNumber);
      if (filesError) throw filesError;
  
      setFiles(filesData || []);
      closeUploadModal();
      toast.success('File uploaded successfully!');
    } catch (err) {
      console.error('Error uploading file:', err);
      toast.error(`Failed to upload file: ${err.message}`);
    }
  };

  const handleFileDownload = async (filePath) => {
    try {
      const { data, error } = await supabase
        .storage
        .from('job-files')
        .createSignedUrl(filePath, 60);
      if (error) throw new Error(`Failed to generate signed URL: ${error.message}`);
  
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error downloading file:', err);
      toast.error(`Failed to download file: ${err.message}`);
    }
  };

  const refreshKanbanJobs = async () => {
    try {
      const { data: refreshedKanbanJobs, error: fetchError } = await supabase
        .from('kanban_jobs')
        .select(`
          id, po_number, po_line_number, part_number, description, status, 
          quantity_on_order, produced_quantity, shipped_quantity, 
          po_start_date, unit_price, 
          shops(id, name), customers(id, nickname, name, 
            billing_address, billing_city, billing_state, billing_zip, 
            billing_contact_name, billing_contact_email, billing_contact_phone,
            shipping_address, shipping_city, shipping_state, shipping_zip,
            shipping_contact_name, shipping_contact_email, shipping_contact_phone, net_terms),
          parts!inner(part_number, inventory_quantity, part_number_master, revision)
        `)
        .eq('po_number', poNumber)
        .order('po_line_number');
      if (fetchError) throw new Error(`Failed to fetch updated Kanban jobs: ${fetchError.message}`);
  
      setKanbanJobs(refreshedKanbanJobs.map(job => ({
        id: job.id,
        po_number: job.po_number,
        po_line_number: job.po_line_number,
        part_number: job.part_number,
        part_number_master: job.parts?.part_number_master || job.part_number,
        description: job.description,
        status: job.status,
        quantity_on_order: job.quantity_on_order,
        produced_quantity: job.produced_quantity,
        shipped_quantity: job.shipped_quantity,
        po_start_date: job.po_start_date,
        unit_price: job.unit_price || 0,
        shop_name: job.shops?.name || 'Unknown',
        customer_name: job.customers?.nickname || 'Unknown',
        customers: job.customers, // Ensure this matches the expected key
        inventory_quantity: job.parts?.inventory_quantity || 0,
      })) || []);
    } catch (err) {
      console.error('Error refreshing kanban jobs:', err);
      toast.error(`Failed to refresh kanban jobs: ${err.message}`);
    }
  };

  // Calculate overall progress
  const totalOrdered = kanbanJobs.reduce((sum, job) => sum + job.quantity_on_order, 0);
  const totalShipped = kanbanJobs.reduce((sum, job) => sum + job.shipped_quantity, 0);
  const overallProgress = totalOrdered > 0 ? Math.round((totalShipped / totalOrdered) * 100) : 0;

  if (loading) return <div className="text-center text-slate-500 py-10">Loading...</div>;

  if (!poNumber) return <div className="text-center text-red-500 py-10">Kanban job not found.</div>;

  return (
    <div className="p-4 bg-slate-50 min-h-screen">
      <div className="w-full mx-auto">
        {/* Unified Header */}
        <div className="bg-white rounded-sm border border-slate-200 mb-4">
          <div className="px-4 py-3 flex justify-between items-center border-b border-slate-100">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/kanban" className="text-slate-500 hover:text-slate-700 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Kanban POs
              </Link>
              <span className="text-slate-300">/</span>
              <span className="px-2 py-0.5 text-xs font-medium bg-violet-100 text-violet-700 rounded">Kanban</span>
              <span className="font-semibold text-slate-800">#{poNumber}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => kanbanJobs.length > 0 && openEditPoModal(kanbanJobs[0])}
                className="inline-flex items-center px-2.5 py-1.5 text-xs bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors"
              >
                <PencilIcon className="w-3.5 h-3.5 mr-1" />
                Edit
              </button>
              <button
                onClick={() => handleDeleteKanbanJob()}
                className="inline-flex items-center px-2.5 py-1.5 text-xs bg-red-50 text-red-700 rounded-sm hover:bg-red-100 transition-colors"
              >
                <TrashIcon className="w-3.5 h-3.5 mr-1" />
                Delete
              </button>
            </div>
          </div>

          {/* Overview Stats */}
          <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-xs text-slate-500">Customer</p>
              <p className="text-sm font-medium text-slate-800 truncate">{kanbanJobs[0]?.customer_name || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Shop</p>
              <p className="text-sm font-medium text-slate-800">{kanbanJobs[0]?.shop_name || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Line Items</p>
              <p className="text-sm font-medium text-slate-800">{kanbanJobs.length}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Work Orders</p>
              <p className="text-sm font-medium text-slate-800">{workOrders.length}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Overall Shipped</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all duration-300"
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-slate-800 tabular-nums">{overallProgress}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-sm border border-slate-200 mb-4">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Line Items</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Ln</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Part</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Description</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Price</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Shipped</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Inv</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {kanbanJobs.map((job) => {
                  const shipProgress = job.quantity_on_order > 0
                    ? Math.round((job.shipped_quantity / job.quantity_on_order) * 100)
                    : 0;
                  return (
                    <tr key={job.id} className="hover:bg-slate-50 transition-colors duration-100">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700">{job.po_line_number}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 font-mono">{job.part_number}</td>
                      <td className="px-3 py-2 text-sm text-slate-600 max-w-[200px] truncate" title={job.description}>{job.description}</td>
                      <td className="px-3 py-2 text-sm text-slate-700 tabular-nums">${job.unit_price?.toFixed(2) || '0.00'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 tabular-nums">{job.quantity_on_order}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all duration-300 ${shipProgress >= 100 ? 'bg-emerald-500' : 'bg-slate-400'}`}
                              style={{ width: `${Math.min(shipProgress, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-600 tabular-nums">{job.shipped_quantity}/{job.quantity_on_order}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 tabular-nums">{job.inventory_quantity}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-sm border border-slate-200 mb-4">
          <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center">
            <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Work Orders</h2>
            <button
              onClick={openCreateWorkOrderModal}
              className="inline-flex items-center px-2.5 py-1 text-xs bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5 mr-1" />
              Create
            </button>
          </div>
          {workOrders.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-500">
              No work orders yet.
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Work ID</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Ln</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Part</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Produced</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Due</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Material</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {workOrders.map((workOrder) => (
                  <tr
                    key={workOrder.id}
                    className="hover:bg-slate-50 transition-colors duration-100 cursor-pointer"
                    onClick={() => {
                      setSelectedWorkOrderForDrawer(workOrder);
                      setIsWorkOrderDrawerOpen(true);
                    }}
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-slate-700">
                      {`${workOrder.po_number}-${workOrder.po_line_number}`}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700">{workOrder.kanban_jobs.po_line_number}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 font-mono">{workOrder.kanban_jobs.part_number}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 tabular-nums">{workOrder.quantity_on_order}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 tabular-nums">{workOrder.produced_quantity}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700">{new Date(workOrder.expected_delivery_date).toLocaleDateString()}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <StatusChip status={workOrder.status} size="xs" />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                      <button
                        onClick={(e) => { e.stopPropagation(); openMaterialDrawer(workOrder); }}
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm font-medium cursor-pointer transition-colors ${
                          workOrder.material_status === 'no_stock'
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : workOrder.material_status === 'ordered'
                            ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                            : workOrder.material_status === 'ready'
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                        title={workOrder.material_notes || 'Click to edit material info'}
                      >
                        {workOrder.material_status === 'no_stock' ? (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                            No Stock
                          </>
                        ) : workOrder.material_status === 'ordered' ? (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                            Ordered
                          </>
                        ) : workOrder.material_status === 'ready' ? (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                            Ready
                          </>
                        ) : (
                          '—'
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          setWorkOrderToDelete(workOrder);
                          setIsDeleteWorkOrderModalOpen(true);
                        }}
                        className="text-xs text-red-600 hover:text-red-800 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {/* Deliveries */}
        <div className="bg-white rounded-sm border border-slate-200 mb-4">
          <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center">
            <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Deliveries</h2>
            <button
              onClick={() => setIsNewDeliveryModalOpen(true)}
              className="inline-flex items-center px-2.5 py-1 text-xs bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5 mr-1" />
              New Delivery
            </button>
          </div>
          {deliveries.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-500">No deliveries yet.</div>
          ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">PKG #</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Part</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Work Order</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Ship Date</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Invoice</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {deliveries.map((delivery) => {
                  const linkedWorkOrder = delivery.job_id ? workOrders.find(wo => wo.id === delivery.job_id) : null;
                  const linkedKanbanJob = delivery.kanban_job_id ? kanbanJobs.find(j => j.id === delivery.kanban_job_id) : null;
                  return (
                  <tr
                    key={delivery.delivery_id}
                    className="hover:bg-slate-50 transition-colors duration-100 cursor-pointer"
                    onClick={() => {
                      setSelectedPackage(delivery);
                      setIsPackageDrawerOpen(true);
                    }}
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-mono font-medium text-slate-700">{delivery.package_number}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-mono text-slate-700">
                      {linkedKanbanJob?.part_number || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">
                      {linkedWorkOrder ? (
                        <span className="font-mono text-xs">{linkedWorkOrder.po_number}-{linkedWorkOrder.po_line_number}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 tabular-nums">{delivery.quantity}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{new Date(delivery.ship_date).toLocaleDateString()}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {delivery.invoice_number ? (
                        <span className="px-1.5 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-sm border border-emerald-200">
                          {delivery.invoice_number}
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 text-xs font-medium bg-slate-100 text-slate-500 rounded-sm">
                          Uninvoiced
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right space-x-2">
                      {delivery.packing_slip_path && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewPDF(delivery.packing_slip_path, 'delivery-files');
                          }}
                          className="text-xs text-slate-600 hover:text-slate-900 hover:underline"
                        >
                          Preview
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDelivery(delivery);
                        }}
                        className="text-xs text-red-600 hover:text-red-800 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {/* Files */}
        <div className="bg-white rounded-sm border border-slate-200 mb-4">
          <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center">
            <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Files</h2>
            <button
              onClick={openUploadModal}
              className="inline-flex items-center px-2.5 py-1 text-xs bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5 mr-1" />
              Upload
            </button>
          </div>
          {files.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-500">No files uploaded.</div>
          ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">File</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Uploaded</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {files.map((file) => (
                  <tr key={file.id} className="hover:bg-slate-50 transition-colors duration-100">
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700">{file.file_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{file.file_type}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{new Date(file.uploaded_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleFileDownload(file.file_path)}
                        className="text-xs text-slate-600 hover:text-slate-900 hover:underline"
                      >
                        Preview
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        <Transition appear show={isCreateWorkOrderModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeCreateWorkOrderModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-slate-900/50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white border border-slate-200 shadow-xl transition-all">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                      <Dialog.Title as="h3" className="text-sm font-semibold text-slate-800">
                        Create Work Order
                      </Dialog.Title>
                      <button onClick={closeCreateWorkOrderModal} className="text-slate-400 hover:text-slate-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Line Item</label>
                        <select
                          value={newWorkOrderLineItem?.id || ''}
                          onChange={(e) => {
                            const selectedJob = kanbanJobs.find(job => job.id === parseInt(e.target.value));
                            setNewWorkOrderLineItem(selectedJob);
                          }}
                          className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        >
                          <option value="">Select a line item</option>
                          {kanbanJobs.map((job) => (
                            <option key={job.id} value={job.id}>
                              Line {job.po_line_number}: {job.part_number}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Quantity</label>
                        <input
                          type="number"
                          value={newQuantityOrdered}
                          onChange={(e) => setNewQuantityOrdered(e.target.value)}
                          placeholder="Enter quantity"
                          className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Due Date</label>
                        <input
                          type="date"
                          value={newDueDate}
                          onChange={(e) => setNewDueDate(e.target.value)}
                          className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
                      <button
                        onClick={closeCreateWorkOrderModal}
                        className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateWorkOrder}
                        className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                      >
                        Create
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        <Transition appear show={isLogShipmentModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeLogShipmentModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
                      Log Shipment for Work Order {selectedWorkOrder?.po_number}-{selectedWorkOrder?.po_line_number} (Line {selectedWorkOrder?.kanban_jobs.po_line_number})
                    </Dialog.Title>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Shipped</label>
                        <input
                          type="number"
                          value={shipmentQuantity}
                          onChange={(e) => setShipmentQuantity(e.target.value)}
                          placeholder="Enter quantity shipped"
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          min="1"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={closeLogShipmentModal}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleLogShipment}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                      >
                        Log Shipment
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

       

        <Transition appear show={isUploadModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeUploadModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-slate-900/50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white border border-slate-200 shadow-xl transition-all">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                      <Dialog.Title as="h3" className="text-sm font-semibold text-slate-800">
                        Upload File
                      </Dialog.Title>
                      <button onClick={closeUploadModal} className="text-slate-400 hover:text-slate-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="p-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Select File</label>
                        <input
                          type="file"
                          onChange={(e) => setFileToUpload(e.target.files[0])}
                          className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:text-sm file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
                      <button
                        onClick={closeUploadModal}
                        className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleFileUpload}
                        className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                      >
                        Upload
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        <Transition appear show={isPdfPreviewModalOpen} as={Fragment}>
  <Dialog as="div" className="relative z-50" onClose={closePdfPreviewModal}>
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
    </Transition.Child>

    <div className="fixed inset-0 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
              Packing Slip Preview
            </Dialog.Title>
            <div className="mb-6">
              {pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  title="Packing Slip Preview"
                  className="w-full h-[600px] border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-500">Loading PDF...</p>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={closePdfPreviewModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>
  </Dialog>
</Transition>

<Transition appear show={isEditPoModalOpen} as={Fragment}>
  <Dialog as="div" className="relative z-50" onClose={closeEditPoModal}>
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-200"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-slate-900/50" />
    </Transition.Child>

    <div className="fixed inset-0 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-sm bg-white border border-slate-200 shadow-xl transition-all">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
              <Dialog.Title as="h3" className="text-base font-semibold text-slate-900">
                Edit PO #{editedPoNumber}
              </Dialog.Title>
              <button onClick={closeEditPoModal} className="text-slate-400 hover:text-slate-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 max-h-[70vh] overflow-y-auto">
              {/* PO Number & Customer */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">PO Number</label>
                  <input
                    type="text"
                    value={editedPoNumber}
                    onChange={(e) => setEditedPoNumber(e.target.value)}
                    className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Customer</label>
                  <select
                    value={editedCustomerId}
                    onChange={(e) => setEditedCustomerId(e.target.value)}
                    className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                  >
                    <option value="">Select customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.nickname || customer.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Add New Line Item */}
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Add Line Item</h4>
                <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                  <div className="grid grid-cols-7 gap-2">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Line #</label>
                      <input
                        type="number"
                        value={newLineItem.po_line_number}
                        onChange={(e) => setNewLineItem({ ...newLineItem, po_line_number: parseInt(e.target.value) || '' })}
                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums bg-white"
                        placeholder="Auto"
                        min="1"
                      />
                    </div>
                    <div className="col-span-2 relative">
                      <label className="block text-xs text-slate-500 mb-1">Part</label>
                      <input
                        type="text"
                        value={partsSearchQuery}
                        onChange={(e) => {
                          setPartsSearchQuery(e.target.value);
                          // Clear selection if user starts typing something different
                          if (newLineItem.part_number && e.target.value !== newLineItem.part_number) {
                            setNewLineItem({
                              ...newLineItem,
                              part_number: '',
                              description: '',
                              unit_price: '',
                            });
                          }
                        }}
                        placeholder="Search parts..."
                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white font-mono"
                      />
                      {partsSearchQuery && !newLineItem.part_number && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-48 overflow-y-auto">
                          {parts
                            .filter(p =>
                              p.part_number.toLowerCase().includes(partsSearchQuery.toLowerCase()) ||
                              (p.description && p.description.toLowerCase().includes(partsSearchQuery.toLowerCase()))
                            )
                            .slice(0, 10)
                            .map((part) => (
                              <button
                                key={part.part_number}
                                type="button"
                                onClick={() => {
                                  setNewLineItem({
                                    ...newLineItem,
                                    part_number: part.part_number,
                                    description: part.description || '',
                                    unit_price: part.unit_price || '',
                                  });
                                  setPartsSearchQuery(part.part_number);
                                }}
                                className="w-full px-2 py-1.5 text-left text-sm hover:bg-slate-100 flex justify-between items-center"
                              >
                                <span className="font-mono">{part.part_number}</span>
                                <span className="text-slate-500 text-xs truncate ml-2">{part.description?.substring(0, 25) || ''}</span>
                              </button>
                            ))}
                          {parts.filter(p =>
                            p.part_number.toLowerCase().includes(partsSearchQuery.toLowerCase()) ||
                            (p.description && p.description.toLowerCase().includes(partsSearchQuery.toLowerCase()))
                          ).length === 0 && (
                            <div className="px-2 py-1.5 text-sm text-slate-500">No parts found</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Qty</label>
                      <input
                        type="number"
                        value={newLineItem.quantity_on_order}
                        onChange={(e) => setNewLineItem({ ...newLineItem, quantity_on_order: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums bg-white"
                        min="1"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Price</label>
                      <input
                        type="number"
                        value={newLineItem.unit_price || ''}
                        onChange={(e) => setNewLineItem({ ...newLineItem, unit_price: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums bg-white"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Shop</label>
                      <select
                        value={newLineItem.shop_id}
                        onChange={(e) => setNewLineItem({ ...newLineItem, shop_id: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                      >
                        <option value="">-</option>
                        {shops.map((shop) => (
                          <option key={shop.id} value={shop.id}>{shop.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => {
                          if (newLineItem.part_number && newLineItem.quantity_on_order) {
                            // Auto-assign line number if not provided
                            const lineNum = newLineItem.po_line_number || (Math.max(0, ...editedLineItems.map(i => i.po_line_number || 0)) + 1);
                            setEditedLineItems([...editedLineItems, {
                              id: `new-${Date.now()}`,
                              po_line_number: lineNum,
                              part_number: newLineItem.part_number,
                              description: newLineItem.description,
                              quantity_on_order: newLineItem.quantity_on_order,
                              unit_price: newLineItem.unit_price || 0,
                              shop_id: newLineItem.shop_id,
                              isNew: true,
                            }]);
                            setNewLineItem({
                              po_line_number: '',
                              part_number: '',
                              description: '',
                              quantity_on_order: '',
                              unit_price: '',
                              shop_id: '',
                            });
                            setPartsSearchQuery('');
                          }
                        }}
                        disabled={!newLineItem.part_number || !newLineItem.quantity_on_order}
                        className="w-full px-2 py-1 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Line Items Table */}
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Line Items</h4>
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 w-16">Line</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Part #</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Description</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 w-20">Qty</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 w-24">Price</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 w-32">Shop</th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600 w-16"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {editedLineItems.map((item, index) => (
                        <tr key={item.id} className={lineItemsToDelete.includes(item.id) ? 'opacity-40 bg-red-50' : ''}>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              value={item.po_line_number || ''}
                              onChange={(e) => {
                                const updatedItems = [...editedLineItems];
                                updatedItems[index] = { ...updatedItems[index], po_line_number: parseInt(e.target.value) || '' };
                                setEditedLineItems(updatedItems);
                              }}
                              disabled={lineItemsToDelete.includes(item.id)}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums disabled:bg-slate-100"
                              min="1"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="text"
                              value={item.part_number}
                              onChange={(e) => {
                                const updatedItems = [...editedLineItems];
                                updatedItems[index] = { ...updatedItems[index], part_number: e.target.value };
                                setEditedLineItems(updatedItems);
                              }}
                              disabled={lineItemsToDelete.includes(item.id)}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 font-mono disabled:bg-slate-100"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => {
                                const updatedItems = [...editedLineItems];
                                updatedItems[index] = { ...updatedItems[index], description: e.target.value };
                                setEditedLineItems(updatedItems);
                              }}
                              disabled={lineItemsToDelete.includes(item.id)}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:bg-slate-100"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              value={item.quantity_on_order}
                              onChange={(e) => {
                                const updatedItems = [...editedLineItems];
                                updatedItems[index] = { ...updatedItems[index], quantity_on_order: e.target.value };
                                setEditedLineItems(updatedItems);
                              }}
                              disabled={lineItemsToDelete.includes(item.id)}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums disabled:bg-slate-100"
                              min="1"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              value={item.unit_price || ''}
                              onChange={(e) => {
                                const updatedItems = [...editedLineItems];
                                updatedItems[index] = { ...updatedItems[index], unit_price: e.target.value };
                                setEditedLineItems(updatedItems);
                              }}
                              disabled={lineItemsToDelete.includes(item.id)}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums disabled:bg-slate-100"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <select
                              value={item.shop_id || ''}
                              onChange={(e) => {
                                const updatedItems = [...editedLineItems];
                                updatedItems[index] = { ...updatedItems[index], shop_id: e.target.value };
                                setEditedLineItems(updatedItems);
                              }}
                              disabled={lineItemsToDelete.includes(item.id)}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:bg-slate-100"
                            >
                              <option value="">-</option>
                              {shops.map((shop) => (
                                <option key={shop.id} value={shop.id}>{shop.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {lineItemsToDelete.includes(item.id) ? (
                              <button
                                type="button"
                                onClick={() => setLineItemsToDelete(lineItemsToDelete.filter(id => id !== item.id))}
                                className="text-xs text-slate-600 hover:text-slate-800"
                              >
                                Undo
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setLineItemsToDelete([...lineItemsToDelete, item.id])}
                                className="text-xs text-red-600 hover:text-red-800"
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
              <button
                onClick={closeEditPoModal}
                className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditPo}
                className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>
  </Dialog>
</Transition>
      </div>

      <CreateDelivery
  isOpen={isNewDeliveryModalOpen}
  onClose={() => setIsNewDeliveryModalOpen(false)}
  jobsData={kanbanJobs}
  poNumber={poNumber}
  deliveriesData={deliveries}
  setDeliveriesData={setDeliveries}
  isKanban={true}
  usePartNumberMaster={true} // KanbanJob uses part_number_master in packing slip
  onDeliveryCreated={refreshKanbanJobs}
  workOrders={workOrders} // Pass work orders to enable PKG → Work Order linking
/>

      {/* Work Order Drawer */}
      <WorkOrderDrawer
        isOpen={isWorkOrderDrawerOpen}
        onClose={() => {
          setIsWorkOrderDrawerOpen(false);
          setSelectedWorkOrderForDrawer(null);
        }}
        workOrder={selectedWorkOrderForDrawer}
        onRefresh={refreshKanbanJobs}
      />

      {/* Package Drawer */}
      <PackageDrawer
        isOpen={isPackageDrawerOpen}
        onClose={() => {
          setIsPackageDrawerOpen(false);
          setSelectedPackage(null);
        }}
        delivery={selectedPackage}
        workOrders={workOrders}
        kanbanJobs={kanbanJobs}
        onPreviewPDF={handlePreviewPDF}
      />

      {/* Delete PO Confirmation Modal */}
      <Transition appear show={isDeletePoModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsDeletePoModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white border border-slate-200 shadow-xl transition-all">
                  <div className="px-4 py-3 border-b border-slate-200 bg-red-50">
                    <Dialog.Title as="h3" className="text-sm font-semibold text-red-900 flex items-center gap-2">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Delete Kanban PO
                    </Dialog.Title>
                  </div>

                  <div className="p-4">
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 mb-2">
                        You are about to permanently delete <span className="font-semibold text-slate-900">PO #{poNumber}</span> and all associated data:
                      </p>
                      <ul className="text-sm text-slate-600 list-disc list-inside ml-2 space-y-1">
                        <li>{kanbanJobs.length} line item{kanbanJobs.length !== 1 ? 's' : ''}</li>
                        <li>{workOrders.length} work order{workOrders.length !== 1 ? 's' : ''}</li>
                        <li>{deliveries.length} deliver{deliveries.length !== 1 ? 'ies' : 'y'}</li>
                      </ul>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-4">
                      <p className="text-xs font-semibold text-red-800 mb-1">⚠️ This action is irreversible</p>
                      <p className="text-xs text-red-700">All data will be permanently deleted and cannot be recovered.</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Type <span className="font-mono font-semibold text-slate-800">{poNumber}</span> to confirm
                      </label>
                      <input
                        type="text"
                        value={deletePoConfirmation}
                        onChange={(e) => setDeletePoConfirmation(e.target.value)}
                        placeholder="Enter PO number"
                        className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-red-400 font-mono"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
                    <button
                      onClick={() => setIsDeletePoModalOpen(false)}
                      className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDeleteKanbanJob}
                      disabled={deletePoConfirmation !== poNumber}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed transition-colors"
                    >
                      Delete PO
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Work Order Confirmation Modal */}
      <Transition appear show={isDeleteWorkOrderModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsDeleteWorkOrderModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white border border-slate-200 shadow-xl transition-all">
                  <div className="px-4 py-3 border-b border-slate-200 bg-red-50">
                    <Dialog.Title as="h3" className="text-base font-semibold text-red-900 flex items-center gap-2">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Delete Work Order
                    </Dialog.Title>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-slate-600 mb-3">
                      Are you sure you want to delete work order{' '}
                      <span className="font-semibold text-slate-900">
                        {workOrderToDelete?.po_number}-{workOrderToDelete?.po_line_number}
                      </span>?
                    </p>
                    <p className="text-sm text-red-600">
                      This action cannot be undone.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors"
                      onClick={() => {
                        setIsDeleteWorkOrderModalOpen(false);
                        setWorkOrderToDelete(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors"
                      onClick={handleDeleteWorkOrder}
                    >
                      Delete
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Material Drawer */}
      <MaterialDrawer
        isOpen={materialDrawerOpen}
        onClose={() => setMaterialDrawerOpen(false)}
        job={selectedWorkOrderForMaterial}
        onUpdate={handleMaterialUpdate}
      />

    </div>
  );
}

export default KanbanJob;